import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { Copy, RotateCcw } from "lucide-react";

import styles from "./Drive.module.scss";
import Spinner from "../../components/spinner";
import { copyText, formatWhen } from "./driveApi";
import { markdownToHtml } from "./markdown";

/**
 * Every version of one item, newest first: who wrote it, when, and the text.
 * Anyone who can see Drive can read and copy an old version; restoring one
 * needs `drive.edit`, and a restore is itself a new version — nothing here
 * can lose text.
 */
const HistoryModal = ({ isOpen, item, api, canRestore, onRestored, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!isOpen || !item) return;
    let live = true;
    setLoading(true);
    api
      .versions(item._id)
      .then((rows) => {
        if (!live) return;
        setVersions(rows || []);
        setSelected(rows?.[0]?.version ?? null);
      })
      .catch((error) => toast.error(error.message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [isOpen, item, api]);

  const shown = versions.find((v) => v.version === selected);

  const restore = async (version) => {
    if (!window.confirm(`Restore version ${version}? The current text is kept as its own version.`)) return;
    setRestoring(true);
    try {
      await api.restore(item._id, version);
      toast.success(`Restored version ${version}.`);
      onRestored();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRestoring(false);
    }
  };

  const copy = async (text) => {
    try {
      await copyText(text);
      toast.success("Copied.");
    } catch {
      toast.error("Couldn't copy — select the text and copy it by hand.");
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="modal_body_md" overlayClassName="modal_overlay">
      <div className="box_wrapper">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">History — {item?.title}</h4>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <div className={styles.history}>
            <ul className={styles.versionList}>
              {versions.map((v) => (
                <li key={v.version}>
                  <button
                    type="button"
                    className={`${styles.versionRow} ${v.version === selected ? styles.active : ""}`}
                    onClick={() => setSelected(v.version)}
                  >
                    <span className={styles.versionNo}>
                      v{v.version}
                      {v.current && <span className={styles.currentTag}>current</span>}
                    </span>
                    <span className={styles.meta}>
                      {formatWhen(v.editedAt)}
                      {v.editedBy?.adminName ? ` · ${v.editedBy.adminName}` : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {shown && (
              <div className={styles.versionBody}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <strong className={styles.versionTitle}>{shown.title}</strong>
                  <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => copy(shown.content)}>
                    <Copy size={14} /> Copy
                  </button>
                  {canRestore && !shown.current && (
                    <button
                      type="button"
                      className="btn btn-sm btn-dark"
                      disabled={restoring}
                      onClick={() => restore(shown.version)}
                    >
                      <RotateCcw size={14} /> Restore
                    </button>
                  )}
                </div>
                <div
                  className={`${styles.markdown} ${styles.content}`}
                  // Sanitised in markdownToHtml.
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(shown.content) || "<p>(empty)</p>" }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default HistoryModal;
